/* eslint-disable no-unused-vars */
import React from 'react';
import { Button, Flex, FlexProps, IconButton, Text } from '@chakra-ui/react';
import { ArrowLeft, ArrowRight, DotsThree } from 'phosphor-react';

const buildPages = (current: number, total: number): (number | 'gap')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

  const pages: (number | 'gap')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) pages.push('gap');
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < total - 1) pages.push('gap');
  pages.push(total);

  return pages;
};

interface PaginationProps extends Omit<FlexProps, 'onChange'> {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
  label?: string;
}

export const Pagination = ({
  currentPage,
  totalPages,
  onChange,
  label = 'Paginação',
  ...rest
}: PaginationProps) => {
  const safeTotal = Math.max(totalPages, 1);
  const safeCurrent = Math.min(Math.max(currentPage, 1), safeTotal);

  return (
    <Flex as="nav" aria-label={label} align="center" gap="4px" {...rest}>
      <Button
        size="sm"
        variant="ghost"
        isDisabled={safeCurrent === 1}
        leftIcon={<ArrowLeft size={14} />}
        onClick={() => onChange(safeCurrent - 1)}
        aria-label="Ir para a página anterior"
      >
        Anterior
      </Button>

      {buildPages(safeCurrent, safeTotal).map((page, index) =>
        page === 'gap' ? (
          <Flex
            // eslint-disable-next-line react/no-array-index-key
            key={`gap-${index}`}
            w="34px"
            h="34px"
            align="center"
            justify="center"
            color="content.faint"
            aria-hidden
          >
            <DotsThree size={16} />
          </Flex>
        ) : (
          <IconButton
            key={page}
            size="sm"
            w="34px"
            h="34px"
            borderRadius="lg"
            fontFamily="body"
            fontSize="13px"
            variant={page === safeCurrent ? 'outline' : 'ghost'}
            color={page === safeCurrent ? 'content.strong' : 'content.body'}
            aria-label={`Página ${page}`}
            aria-current={page === safeCurrent ? 'page' : undefined}
            onClick={() => onChange(page)}
            icon={<span>{page}</span>}
          />
        )
      )}

      <Button
        size="sm"
        variant="ghost"
        isDisabled={safeCurrent === safeTotal}
        rightIcon={<ArrowRight size={14} />}
        onClick={() => onChange(safeCurrent + 1)}
        aria-label="Ir para a próxima página"
      >
        Próxima
      </Button>
    </Flex>
  );
};

interface PaginationFooterProps extends Omit<PaginationProps, 'label'> {
  totalItems?: number;
  itemLabel?: string;
  itemLabelPlural?: string;
}

export const PaginationFooter = ({
  totalItems,
  itemLabel = 'registro',
  itemLabelPlural,
  ...paginationProps
}: PaginationFooterProps) => {
  const plural = itemLabelPlural ?? `${itemLabel}s`;
  const count = totalItems ?? 0;

  return (
    <Flex
      align="center"
      justify="space-between"
      flexWrap="wrap"
      gap="8px"
      p="12px"
      borderTop="1px solid"
      borderColor="line.hairline"
    >
      <Text fontFamily="mono" fontSize="12px" color="content.subtle">
        {`${count} ${count === 1 ? itemLabel : plural}`}
      </Text>
      <Pagination {...paginationProps} />
    </Flex>
  );
};
